import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

/**
 * Static meta for displaying brain-type probabilities.
 * Formal (4-letter) code is shown in ALL CAPS with the informal name in brackets,
 * e.g. "ASCP (Perfectionist)".
 */
export const BRAIN_TYPE_LABELS = [
  { id: 1, code: "ASCP", informal: "Perfectionist" },
  { id: 2, code: "SACL", informal: "Helper" },
  { id: 3, code: "SALS", informal: "Succesfull Worker" },
  { id: 4, code: "SSLC", informal: "Romantic" },
  { id: 5, code: "FFSP", informal: "Analyst" },
  { id: 6, code: "FSSC", informal: "Loyalist" },
  { id: 7, code: "FAPS", informal: "Hedonist" },
  { id: 8, code: "AFPC", informal: "Boss" },
  { id: 9, code: "AFCL", informal: "Mediator" },
];

export function formatBrainTypeLabel(id) {
  const meta = BRAIN_TYPE_LABELS.find((m) => m.id === Number(id));
  if (!meta) return `#${id}`;
  return `${meta.code} (${meta.informal})`;
}

/**
 * Renders the 9 brain-type probabilities as a horizontal bar chart + ranked
 * list. Used on the participant survey result screen and on the consultant's
 * "Online" tab.
 *
 * Props:
 *   probabilities: { [id: number]: number }  // percentages (0..100)
 *   topId:         number  (optional, highlights the top brain type)
 */
export default function BrainTypeProbabilities({ probabilities, topId }) {
  if (!probabilities || Object.keys(probabilities).length === 0) return null;

  // Normalise probabilities keys to numbers and build the chart data
  // preserving the canonical brain-type order (1..9).
  const data = BRAIN_TYPE_LABELS.map((m) => {
    const p =
      probabilities[m.id] ??
      probabilities[String(m.id)] ??
      0;
    return {
      id: m.id,
      label: `${m.id}. ${m.code} (${m.informal})`,
      shortLabel: m.code,
      probability: Number(p) || 0,
    };
  });

  const inferredTop =
    topId ?? data.reduce((a, b) => (b.probability > a.probability ? b : a)).id;

  // Sorted list (descending) for the textual summary.
  const ranked = [...data].sort((a, b) => b.probability - a.probability);

  return (
    <div className="space-y-6" data-testid="brain-type-probabilities">
      <div className="border border-zinc-200 rounded-sm bg-white p-4">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
          Waarschijnlijkheid per breintype
        </div>
        <div className="h-[340px] w-full" data-testid="brain-type-probabilities-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 4, right: 48, left: 8, bottom: 4 }}
            >
              <XAxis
                type="number"
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 11, fill: "#71717a" }}
                axisLine={{ stroke: "#e4e4e7" }}
                tickLine={{ stroke: "#e4e4e7" }}
              />
              <YAxis
                type="category"
                dataKey="label"
                tick={{ fontSize: 12, fill: "#27272a" }}
                axisLine={{ stroke: "#e4e4e7" }}
                tickLine={false}
                width={180}
              />
              <Tooltip
                cursor={{ fill: "rgba(30,136,229,0.06)" }}
                formatter={(value) => [`${value.toFixed(2)}%`, "Waarschijnlijkheid"]}
                labelStyle={{ fontSize: 12, fontWeight: 600 }}
                contentStyle={{
                  fontSize: 12,
                  borderRadius: 2,
                  border: "1px solid #18181b",
                }}
              />
              <Bar dataKey="probability" radius={[0, 2, 2, 0]} barSize={18}>
                {data.map((d) => (
                  <Cell
                    key={d.id}
                    fill={d.id === inferredTop ? "#1E88E5" : "#a1a1aa"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border border-zinc-200 rounded-sm bg-white">
        <div className="px-4 py-2.5 text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-100">
          Ranglijst
        </div>
        <ol className="divide-y divide-zinc-100" data-testid="brain-type-probabilities-list">
          {ranked.map((d, idx) => (
            <li
              key={d.id}
              className={`flex items-center justify-between px-4 py-2.5 text-sm ${
                d.id === inferredTop ? "bg-[#1E88E5]/5" : ""
              }`}
              data-testid={`brain-type-prob-row-${d.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="font-mono text-xs text-zinc-400 w-4 shrink-0">
                  {idx + 1}.
                </span>
                <span className="font-mono text-xs text-zinc-500 w-4 shrink-0">
                  #{d.id}
                </span>
                <span className="font-semibold tracking-wide">
                  {d.shortLabel}
                </span>
                <span className="text-zinc-500 truncate">
                  ({BRAIN_TYPE_LABELS.find((m) => m.id === d.id).informal})
                </span>
              </div>
              <div
                className={`font-display text-sm font-bold tabular-nums ${
                  d.id === inferredTop ? "text-[#1E88E5]" : "text-zinc-700"
                }`}
                data-testid={`brain-type-prob-value-${d.id}`}
              >
                {d.probability.toFixed(2)}%
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
